import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  IconButton,
  Slider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Big } from "big.js";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../context/CurrencyContext";
import { RecoverableCosts } from "../../shared/judgment";
import { TABULAR_NUMERALS } from "../general/dataAtoms/shared";
import { SegmentedControl } from "../general/SegmentedControl";
import { amountOf } from "./CloseStageDialog";
import {
  COST_TYPES,
  isValidAssessment,
  recoveryOf,
  RecoveryDraft,
  shareOf,
  subtotalOf,
  weightedShareOf,
} from "./judgmentDraft";

const amount = { ...TABULAR_NUMERALS, whiteSpace: "nowrap" } as const;
const button = { textTransform: "none", fontWeight: 600 } as const;
// fixed, so that nothing moves as the sliders do
const widths = ["22%", "14%", "19%", "16%", "29%"];
const SLIDER_WIDTH = 80;
const sliderCell = { width: SLIDER_WIDTH, flex: "none" };

/**
 * What a winner recovers from those who lost to it: its costs by type, the
 * shares of them, by cost type or one for all, and the assessment, of its
 * costs or as the sum to recover, which the user can add once it is known.
 */
export const RecoveryOrderTable = ({
  name,
  payers,
  recoverable,
  estimates,
  draft,
  onChange,
}: {
  name: string;
  payers: string;
  recoverable: RecoverableCosts;
  estimates: RecoveryDraft["percent"];
  draft: RecoveryDraft;
  onChange: (draft: RecoveryDraft) => void;
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.timeline.judgment.recoveries",
  });
  const { currency } = useCurrency();
  const money = (value: Big | number) =>
    currency.format(typeof value === "number" ? value : value.toNumber());

  const subtotal = subtotalOf(recoverable, draft);
  const expected = COST_TYPES.reduce(
    (sum, type) =>
      sum.add(
        new Big(recoverable.byType[type] ?? 0)
          .mul(estimates[type] ?? 0)
          .div(100),
      ),
    new Big(0),
  );
  const costsTotal = COST_TYPES.reduce(
    (sum, type) => sum.add(recoverable.byType[type] ?? 0),
    new Big(0),
  );
  const weighted = `${weightedShareOf(recoverable, draft).toFixed(1)}%`;
  const assessed = draft.assessed[draft.of];
  const editing = draft.draft !== undefined;
  const draftAmount =
    editing && draft.draft ? amountOf(draft.draft)?.toNumber() : undefined;

  const versus = (
    value: Big,
    against: Big,
    keys: { same: string; less: string; more: string },
  ) => {
    const diff = value.minus(against);
    return {
      text: diff.eq(0)
        ? t(keys.same)
        : t(diff.lt(0) ? keys.less : keys.more, { amount: money(diff.abs()) }),
      over: diff.gt(0),
    };
  };
  const toExpected = {
    same: "asExpected",
    less: "lessThanExpected",
    more: "moreThanExpected",
  };
  const note = (over: boolean) => ({
    color: over ? "warning.dark" : "text.secondary",
    fontWeight: over ? 600 : undefined,
    whiteSpace: "nowrap",
    fontSize: 13,
  });

  const save = () => {
    const { [draft.of]: _, ...others } = draft.assessed;
    const { draft: __, ...rest } = draft;
    onChange({
      ...rest,
      assessed:
        amountOf(draft.draft ?? "") === undefined
          ? others
          : { ...others, [draft.of]: amountOf(draft.draft!)!.toNumber() },
    });
  };
  const cancel = () => {
    const { draft: _, ...rest } = draft;
    onChange(rest);
  };

  const value = (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{ alignItems: "center", justifyContent: "flex-end" }}
    >
      <Tooltip title={t("assessment.edit")}>
        <IconButton
          size="small"
          aria-label={t("assessment.edit")}
          onClick={() =>
            onChange({ ...draft, draft: assessed?.toString() ?? "" })
          }
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Typography
        component="span"
        sx={{
          ...amount,
          fontWeight: assessed === undefined ? undefined : 700,
          fontSize: assessed === undefined ? 14 : 16,
          color: assessed === undefined ? "text.secondary" : "text.primary",
        }}
      >
        {assessed === undefined ? "--" : money(assessed)}
      </Typography>
    </Stack>
  );
  const field = (
    <TextField
      size="small"
      autoFocus
      value={draft.draft}
      error={!isValidAssessment(draft.draft ?? "")}
      onChange={(event) => onChange({ ...draft, draft: event.target.value })}
      slotProps={{
        htmlInput: {
          inputMode: "decimal",
          "aria-label": t("assessment.amount"),
          style: { textAlign: "right" },
        },
      }}
      sx={{ width: 110 }}
    />
  );
  const outcome =
    assessed === undefined
      ? undefined
      : versus(recoveryOf(recoverable, draft), subtotal, {
          same: "assessment.asSubtotal",
          less: "assessment.lessThanSubtotal",
          more: "assessment.moreThanSubtotal",
        });
  const live =
    draftAmount === undefined
      ? undefined
      : versus(recoveryOf(recoverable, draft, draftAmount), subtotal, {
          same: "assessment.asSubtotal",
          less: "assessment.lessThanSubtotal",
          more: "assessment.moreThanSubtotal",
        });
  const shown = editing ? live : outcome;

  return (
    <Box
      sx={{
        mt: 1.5,
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        useFlexGap
        sx={{
          flexWrap: "wrap",
          gap: 1.5,
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.75,
          py: 1.25,
          bgcolor: "action.hover",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
            {t("recovers", { name, payers })}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            {assessed === undefined
              ? t("about", { amount: money(subtotal) })
              : t("toRecover", {
                  amount: money(recoveryOf(recoverable, draft)),
                })}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {t("sharesLabel")}
          </Typography>
          <SegmentedControl
            ariaLabel={t("sharesLabel")}
            value={draft.mode}
            options={[
              { value: "byType", label: t("shares.byType") },
              { value: "oneShare", label: t("shares.oneShare") },
            ]}
            onChange={(mode) => onChange({ ...draft, mode })}
          />
        </Stack>
      </Stack>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ tableLayout: "fixed", minWidth: 760 }}>
          <TableHead>
            <TableRow>
              {(["type", "costs", "share", "recovery", "then"] as const).map(
                (column, index) => (
                  <TableCell
                    key={column}
                    align={
                      column === "costs" || column === "recovery"
                        ? "right"
                        : "left"
                    }
                    sx={{ width: widths[index] }}
                  >
                    {t(`column.${column}`)}
                  </TableCell>
                ),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {COST_TYPES.map((type) => {
              const costs = new Big(recoverable.byType[type] ?? 0);
              const share = shareOf(draft, type);
              const recovered = costs.mul(share).div(100);
              const compared = versus(
                recovered,
                costs.mul(estimates[type] ?? 0).div(100),
                toExpected,
              );
              return (
                <TableRow key={type}>
                  <TableCell component="th" scope="row">
                    {t(`costTypes.${type}`)}
                  </TableCell>
                  <TableCell align="right" sx={amount}>
                    {money(costs)}
                  </TableCell>
                  <TableCell>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      sx={{ alignItems: "center" }}
                    >
                      <Box sx={sliderCell}>
                        {draft.mode === "byType" && (
                          <Slider
                            size="small"
                            min={0}
                            max={100}
                            value={share}
                            aria-label={t("shareOf", {
                              type: t(`costTypes.${type}`),
                            })}
                            onChange={(_, percent) =>
                              onChange({
                                ...draft,
                                percent: {
                                  ...draft.percent,
                                  [type]: percent as number,
                                },
                              })
                            }
                          />
                        )}
                      </Box>
                      <Box component="span" sx={amount}>
                        {share}%
                      </Box>
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={amount}>
                    {money(recovered)}
                  </TableCell>
                  <TableCell sx={note(compared.over)}>
                    {compared.text}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell
                component="th"
                scope="row"
                sx={{ color: "text.primary", fontWeight: 600, fontSize: 14 }}
              >
                {t("subtotal")}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  ...amount,
                  color: "text.primary",
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                {money(costsTotal)}
              </TableCell>
              <TableCell
                sx={{ color: "text.primary", fontWeight: 600, fontSize: 14 }}
              >
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ alignItems: "center" }}
                >
                  <Box sx={sliderCell}>
                    {draft.mode === "oneShare" && (
                      <Slider
                        size="small"
                        min={0}
                        max={100}
                        value={draft.oneShare}
                        aria-label={t("oneShareOf", { name })}
                        onChange={(_, oneShare) =>
                          onChange({ ...draft, oneShare: oneShare as number })
                        }
                      />
                    )}
                  </Box>
                  <Box component="span" sx={amount}>
                    {weighted}
                  </Box>
                </Stack>
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  ...amount,
                  color: "text.primary",
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                {money(subtotal)}
              </TableCell>
              <TableCell sx={note(subtotal.gt(expected))}>
                {versus(subtotal, expected, toExpected).text}
              </TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: "action.hover" }}>
              <TableCell colSpan={5}>
                <Stack
                  direction="row"
                  useFlexGap
                  sx={{
                    flexWrap: "wrap",
                    gap: 1.5,
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: "text.primary",
                      }}
                    >
                      {t("assessment.title")}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                      {t(
                        draft.of === "costs"
                          ? "assessment.costsHelp"
                          : "assessment.recoveryHelp",
                      )}
                    </Typography>
                  </Box>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "center" }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      {t("assessment.of")}
                    </Typography>
                    <SegmentedControl
                      ariaLabel={t("assessment.of")}
                      value={draft.of}
                      options={[
                        { value: "costs", label: t("assessment.costs") },
                        { value: "recovery", label: t("assessment.recovery") },
                      ]}
                      onChange={(of) => {
                        const { draft: _, ...rest } = draft;
                        onChange({ ...rest, of });
                      }}
                    />
                  </Stack>
                </Stack>
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell
                component="th"
                scope="row"
                sx={{
                  color: "text.primary",
                  fontWeight: 600,
                  fontSize: 14,
                  whiteSpace: "nowrap",
                }}
              >
                {t(
                  draft.of === "costs"
                    ? "assessment.assessedCosts"
                    : "assessment.assessedRecovery",
                )}
              </TableCell>
              <TableCell align="right" sx={{ pl: 0 }}>
                {draft.of === "costs" && (editing ? field : value)}
              </TableCell>
              <TableCell
                sx={{
                  ...amount,
                  color: "text.primary",
                  fontWeight: 600,
                  fontSize: 14,
                }}
              >
                {draft.of === "costs" && assessed !== undefined && !editing && (
                  <Stack direction="row" spacing={1.5}>
                    <Box sx={sliderCell} />
                    <span>{weighted}</span>
                  </Stack>
                )}
              </TableCell>
              <TableCell align="right" sx={{ pl: 0.5 }}>
                {draft.of === "recovery" && (editing ? field : value)}
                {draft.of === "costs" && assessed !== undefined && !editing && (
                  <Typography
                    component="span"
                    sx={{
                      ...amount,
                      fontSize: 16,
                      fontWeight: 700,
                      color: "text.primary",
                    }}
                  >
                    {money(recoveryOf(recoverable, draft))}
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={note(shown?.over ?? false)}>
                {editing ? (
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="contained"
                      sx={button}
                      disabled={!isValidAssessment(draft.draft ?? "")}
                      onClick={save}
                    >
                      {t("assessment.save")}
                    </Button>
                    <Button size="small" sx={button} onClick={cancel}>
                      {t("assessment.cancel")}
                    </Button>
                  </Stack>
                ) : (
                  shown?.text
                )}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </Box>
    </Box>
  );
};
